import { Component, ChangeDetectionStrategy, computed, DestroyRef, inject, OnInit, signal, ViewContainerRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, filter, firstValueFrom, switchMap, take } from 'rxjs';
import { MatBottomSheet, MatBottomSheetConfig } from '@angular/material/bottom-sheet';
import { ComponentType } from '@angular/cdk/portal';
import { CsIconModule } from '@components/icon';
import { ButtonCtaComponent } from '@components/button-cta/button-cta.component';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { EventLoggerService } from '@services/event-logger-service';
import { RegimenService } from '@services/regimen-service';
import { ShiprocketService } from '@services/shiprocket-service';
import { CheckoutService } from '@services/checkout-service';
import { ExperimentService } from '@services/experiment/experiment.service';
import { RegimenReorderService } from '@services/regimen-reorder-service/regimen-reorder.service';
import { Regimen, ReorderKitDetail, User } from '@cureskin/api-client/src/server-api';
import { ApiClientConstant } from '@cureskin/api-client/src/constant/api-client.constant';
import { AddressBookI } from '@store/modals';
import { fromActions } from '@store/actions';
import { fromSelectors } from '@store/selectors';
import { RegimenDetailsComponent } from './regimen-details/regimen-details.component';
import { RegimenReorderCardComponent } from './regimen-reorder-card/regimen-reorder-card.component';
import { ToPayComponent, PricingDetails } from '@shared/to-pay/to-pay.component';
import { RegimenPriceDetailsData, RegimenReorderPriceDetailsData } from '@shared/bottom-sheet-layouts/price-details-bottomsheet/price-details-bottomsheet.component';
import { PRICE_DETAILS_BOTTOMSHEET_TYPE, SHIPROCKET_FILL_ADDRESS_ACTION, ShiprocketFillAddressDismissResult } from '../app.constants';
import { UserService } from '@services/user/user.service';
import { LoadingModule } from '@components/loading';
import { DeliveryAddressBarComponent } from '@shared/delivery-address-bar/delivery-address-bar.component';
import { ExpectedDeliveryDateComponent } from '@shared/expected-delivery-date/expected-delivery-date.component';
import { SocialProofBarComponent } from '@shared/social-proof-bar/social-proof-bar.component';
import { ShiprocketFillAddressBottomsheetComponent } from './shiprocket-fill-address-bottomsheet/shiprocket-fill-address-bottomsheet.component';
import { RegimenPricingExperiment } from '@services/experiment/experiment.service.types';
import { AppConfig } from '../app.config';
import { CurrentComponentService } from '@services/current-component';
import {
  createAddressFieldErrors, createNameValidator, createTextFieldValidator,
} from '@shared/address-form/address-field-validators';
import {
  AddressAutofillService, AutofillFocus, DetectFailure, DetectionState,
} from '../user-address/services/address-autofill.service';
import { AddressAutofillHandoffService } from '../user-address/services/address-autofill-handoff.service';
import { AddressLocationSheetComponent, LocationSheetResult } from '../user-address/address-location-sheet/address-location-sheet.component';
import {
  RemainingDetailsSheetComponent, RemainingDetailsSheetResult,
} from '../user-address/remaining-details-sheet/remaining-details-sheet.component';

const NOT_ABLE_TO_DETECT_LOCATION: string = $localize`:@@notAbleToDetectLocation:Not able to detect location`;
const LOCATION_PERMISSION_DENIED: string = 'Location permission denied. Kindly enable permission in your browser settings.';
const AUTOFILL_SHEET_CONFIG: { backdropClass: string } = { backdropClass: 'address-autofill-sheet-backdrop' };
const REVIEW_ORDER_ENTRY: string = 'review_order';

@Component({
  selector: 'app-review-order',
  imports: [
    CommonModule,
    CsIconModule,
    ButtonCtaComponent,
    RegimenDetailsComponent,
    RegimenReorderCardComponent,
    ToPayComponent,
    LoadingModule,
    DeliveryAddressBarComponent,
    ExpectedDeliveryDateComponent,
    SocialProofBarComponent,
  ],
  templateUrl: './review-order.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AddressAutofillService],
})
export class ReviewOrderComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly connectionService = inject(ConnectionService);
  private readonly broadcast = inject(BroadcastService);
  private readonly appBridgeService = inject(AppWebBridgeService);
  private readonly eventLoggerService = inject(EventLoggerService);
  private readonly regimenService = inject(RegimenService);
  private readonly userService = inject(UserService);
  private readonly store = inject(Store);
  private readonly bottomSheet = inject(MatBottomSheet);
  private readonly shiprocketService = inject(ShiprocketService);
  private readonly checkoutService = inject(CheckoutService);
  private readonly experimentService = inject(ExperimentService);
  private readonly regimenReorderService = inject(RegimenReorderService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly appConfig = inject(AppConfig);
  private readonly autofill = inject(AddressAutofillService);
  private readonly handoff = inject(AddressAutofillHandoffService);
  private readonly currentComponent = inject(CurrentComponentService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  readonly ApiClientConstant = ApiClientConstant;

  checkoutInProgress$: Observable<boolean> = this.store.select(fromSelectors.selectCheckoutProgress);

  user = signal<User | null>(null);
  regimen = signal<Regimen | null>(null);
  loading = signal<boolean>(true);
  defaultAddress = signal<AddressBookI | null>(null);
  shiprocketAddressExists = signal<boolean>(false);
  isRegimenReorder = signal<boolean>(false);
  reorderKit = signal<ReorderKitDetail | null>(null);
  regimenClass = signal<string>('');

  showDoctorCharges = computed<boolean>(() =>
    this.experimentService.regimenReorderExperiment?.variant?.showDoctorCharges ?? false);

  checkoutKitImage = computed<string>(() =>
    this.regimenClass() === ApiClientConstant.Regimen.Class.HAIR
      ? 'https://cdn.cureskin.com/app/img/dr_holding_purple_kit.png'
      : 'https://cdn.cureskin.com/app/img/dr_holding_orange_kit.png');

  hasDefaultAddress = computed(() => !!this.defaultAddress()?.objectId);
  // E2E hook (data-testid): the footer CTA adds an address until one exists, then proceeds to payment.
  checkoutCtaTestId = computed(() => (this.hasDefaultAddress() ? 'proceed-to-pay' : 'add-address'));
  showLocationCtaOnReviewOrder = signal<boolean>(false);
  autofillSaveInProgress = signal<boolean>(false);
  showLocationAutofillCta = computed(() => this.showLocationCtaOnReviewOrder() && !this.hasDefaultAddress());
  expectedDeliveryDate = signal<{ from?: Date; to?: Date }>({});
  orderDeliveryDateToCurrentDateDifference = signal<number | undefined>(undefined);
  newRegimenPricingExperiment: RegimenPricingExperiment;
  shiprocketCheckDone = false;
  checkoutPageOpenLogged = false;

  private reorderPricing = computed<PricingDetails | null>(() => {
    const kit = this.reorderKit();
    if (!kit) return null;
    return { totalMrp: kit.totalMrp || 0, totalSP: kit.totalSP || 0, discountInRupees: kit.discountInRupees || 0 };
  });

  private regimenPricing = computed<PricingDetails | null>(() => {
    const regimen = this.regimen();
    if (!regimen) return null;
    const totalMrp = regimen.fixedPriceMRP || 0;
    const totalSP = regimen.fixedPrice || 0;
    return { totalMrp, totalSP, discountInRupees: totalMrp - totalSP };
  });

  pricingDetails = computed<PricingDetails | null>(() =>
    (this.isRegimenReorder() ? this.reorderPricing() : this.regimenPricing()));

  bottomsheetData = computed<RegimenPriceDetailsData | RegimenReorderPriceDetailsData | null>(() => {
    if (this.isRegimenReorder()) {
      const kit = this.reorderKit();
      if (!kit) return null;
      return {
        type: PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN_REORDER,
        regimenDetails: kit,
        cashUsed: this.regimenReorderService.getCashUsedForClass(this.regimenClass()),
        originalDiscount: this.regimenReorderService.getOriginalDiscountForClass(this.regimenClass()),
        showDoctorCharges: this.showDoctorCharges(),
      };
    }
    const regimen = this.regimen();
    if (!regimen) return null;
    return {
      type: PRICE_DETAILS_BOTTOMSHEET_TYPE.REGIMEN,
      fixedPriceMrp: regimen.fixedPriceMRP || 0,
      fixedPrice: regimen.fixedPrice || 0,
      crossSellDiscount: regimen?.allocationInfo?.crossSellDiscount || 0,
    };
  });

  async ngOnInit(): Promise<void> {
    this.loading.set(true);
    this.appBridgeService.setStatusBarColor('cureBlack');
    this.user.set(this.userService.getUser());
    this.sendOrderLogs();
    const params = this.route.snapshot.queryParams;
    this.isRegimenReorder.set(params?.isRegimenReorder === 'true');
    this.getUserExperiments();
    await this.experimentService.experimentsLoaded;
    this.showLocationCtaOnReviewOrder.set(this.experimentService.shouldShowLocationCtaOnReviewOrder());

    if (this.isRegimenReorder()) {
      const loaded = await this.loadRegimenReorderKit(params?.regimenClass);
      if (!loaded) return; // kit unavailable — loadRegimenReorderKit already navigated back
    } else {
      await this.getRegimen(params?.regimenId);
    }

    this.fetchDefaultAddress();
    this.loading.set(false);
  }

  getUserExperiments() {
    this.newRegimenPricingExperiment = this.experimentService.newRegimenPricingExperiment;
  }

  private async loadRegimenReorderKit(regimenClass: string): Promise<boolean> {
    this.regimenClass.set(regimenClass);
    await this.regimenReorderService.ensureKitDetailsLoaded();
    const kit = this.regimenReorderService.getRegimenReorderKitDetailsForClass(regimenClass);
    if (!kit) {
      this.back();
      return false;
    }
    this.reorderKit.set(kit);
    return true;
  }

  private async getRegimen(regimenId: string): Promise<void> {
    const selectedRegimen = await this.regimenService.getRegimenByRegimenId(regimenId);
    if (selectedRegimen) {
      this.regimen.set(selectedRegimen);
    }
  }

  private fetchDefaultAddress(): void {
    this.store.dispatch(fromActions.AddressBookDefaultFetchBegin({ skipRedirectOnMissing: true }));

    this.store.select(fromSelectors.selectDefaultAddress).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((address: AddressBookI) => {
      this.defaultAddress.set(address || null);
      this.logReviewOrderPageOpen(!!address?.zipCode);
      this.shouldCheckShipRocketAddress(address);
      if (address?.zipCode) {
        this.fetchDeliveryDate(address.zipCode.toString());
      } else {
        const pincode = this.user()?.get('lastLocationPincode');
        if (pincode) {
          this.fetchDeliveryDate(pincode.toString());
        }
      }
    });
  }

  private async fetchDeliveryDate(pincode: string): Promise<void> {
    const result = await this.checkoutService.fetchDeliveryDateInfo(
      pincode,
      this.user()?.get('languagePreference'),
    );
    this.expectedDeliveryDate.set(result.expectedDeliveryDate);
    this.orderDeliveryDateToCurrentDateDifference.set(result.dayDifference);
  }

  shouldCheckShipRocketAddress(address: AddressBookI | null): void {
    if (address || this.shiprocketCheckDone) {
      return;
    }

    this.shiprocketCheckDone = true;
    this.checkShiprocketAddress();
  }

  private async checkShiprocketAddress(): Promise<void> {
    const canProceed = this.experimentService.reviewOrderPageExperiment?.variant?.shouldShowShiprocket && this.shiprocketService.isAcquisitionUser();
    if(!canProceed) {
      return;
    }

    try {
      const response = await this.connectionService.checkIfUserAddressExistsInShipRocket({
        countryCode: '91',
        phone: this.user().get('MobileNumber'),
      });
      this.shiprocketAddressExists.set(response?.exists ?? false);
      if (response?.exists) {
        this.eventLoggerService.cleverTapEvent('shiprocket_address_found', JSON.stringify({ userId: this.user()?.id }));
      }
    } catch {
      this.shiprocketAddressExists.set(false);
    }
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  navigateToAddresses(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'review_order_change_address' }));
    const params = this.checkoutService.buildPreservedAddressParams(
      this.route.snapshot.queryParams,
      { fromCheckout: 'true', fromReviewOrder: 'true' },
    );
    this.connectionService.navigateToURL(this.checkoutService.buildUrlWithParams('/user/addresses', params));
  }

  handleCheckout(): void {
    if (this.hasDefaultAddress()) {
      this.makePayment();
      return;
    }

    if (this.shiprocketAddressExists()) {
      this.handleShiprocketCheckout();
      return;
    }

    this.navigateToAddressEdit();
  }

  private async handleShiprocketCheckout(): Promise<void> {
    const phone = this.user().get('MobileNumber');
    if (!(await this.shiprocketService.isShiprocketAuthorised(phone))) {
      this.openShiprocketFillAddressBottomsheet();
      return;
    }
    let hasAddresses = false;
    try {
      const response = await this.connectionService.getShiprocketAddresses({ phone });
      hasAddresses = !!response?.addresses?.length;
    } catch {
      hasAddresses = false;
    }
    if (hasAddresses) {
      this.checkoutService.navigateToShiprocketAddressList(
        phone,
        this.route.snapshot.queryParams,
        { fromCheckout: 'true', fromReviewOrder: 'true' },
      );
      return;
    }
    this.navigateToAddressEdit();
  }

  private async makePayment(): Promise<void> {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'review_order_make_payment' }), [
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.MOENGAGE,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
    await this.checkoutService.initiateRegimenCheckout(this.route.snapshot.queryParams, this.user());
  }

  private openShiprocketFillAddressBottomsheet(): void {
    const ref = this.bottomSheet.open(ShiprocketFillAddressBottomsheetComponent);
    ref.afterDismissed().subscribe((result: ShiprocketFillAddressDismissResult) => {
      if (!result) return;
      if (result.action === SHIPROCKET_FILL_ADDRESS_ACTION.Manual) {
        this.navigateToAddressEdit();
      } else if (result.action === SHIPROCKET_FILL_ADDRESS_ACTION.Shiprocket) {
        this.openShiprocketOtpFlow();
      }
    });
  }

  private openShiprocketOtpFlow(): void {
    this.shiprocketService.startShiprocketAddressFlow(
      this.bottomSheet,
      this.user().get('MobileNumber'),
      (verifiedPhone: string) => this.checkoutService.navigateToShiprocketAddressList(
        verifiedPhone,
        this.route.snapshot.queryParams,
        { fromCheckout: 'true', fromReviewOrder: 'true' },
      ),
    );
  }

  private sendOrderLogs(): void {
    this.eventLoggerService.trackEvent('checkout_entry', { username: this.user()?.get('username') });
    this.eventLoggerService.trackInElasticSearch({
      username: this.user()?.get('username'),
      added: new Date(),
      type: 'webApp',
      message: 'User into checkout page',
      event: 'CHECKOUT_ENTRY',
    });
  }

  private logReviewOrderPageOpen(defaultAddressExists: boolean): void {
    if (this.checkoutPageOpenLogged) return;
    this.checkoutPageOpenLogged = true;
    const regimenId = this.route.snapshot.queryParams?.regimenId;
    this.eventLoggerService.cleverTapEvent('pageOpen', JSON.stringify({
      regimenId,
      pageName: 'ReviewOrderPage',
      defaultAddressExists,
    }), [
      this.appConfig.Shared.EventTracker.MIXPANEL,
      this.appConfig.Shared.EventTracker.AMPLITUDE,
      this.appConfig.Shared.EventTracker.CLEVER_TAP,
      this.appConfig.Shared.EventTracker.CURESKIN_METRICS,
    ]);
  }

  private navigateToAddressEdit(): void {
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'review_order_add_address' }));
    const params = this.checkoutService.buildPreservedAddressParams(
      this.route.snapshot.queryParams,
      { fromCheckout: 'true', fromReviewOrder: 'true' },
    );
    this.connectionService.navigateToURL(this.checkoutService.buildUrlWithParams('/user/address/new', params));
  }

  async startLocationAutofill(): Promise<void> {
    if (this.autofill.state() === DetectionState.Detecting || this.autofillSaveInProgress()) return;
    this.logAutofillEvent('location_sheet_opened');
    const sheetRef = this.bottomSheet.open(AddressLocationSheetComponent, { ...AUTOFILL_SHEET_CONFIG, viewContainerRef: this.viewContainerRef });
    const sheet = sheetRef.instance;
    this.currentComponent.set(sheet);
    const dismissed = firstValueFrom(sheetRef.afterDismissed());
    dismissed.then((): void => this.autofill.cancel());

    const outcome = await this.autofill.detect({ withDelivery: true, languagePreference: this.user()?.get('languagePreference') });
    if (!outcome.detected && outcome.reason !== DetectFailure.Cancelled) {
      sheetRef.dismiss();
      this.notifyDetectFailure(outcome.reason);
    }
    const result = await dismissed;
    this.currentComponent.remove(sheet);
    if (!outcome.detected) return;
    await this.completeDetectedLocation(result);
  }

  private async completeDetectedLocation(firstConfirmResult: unknown): Promise<void> {
    const fieldErrors = createAddressFieldErrors();
    const form = new FormGroup({
      buildingDetails: new FormControl('', [createTextFieldValidator('buildingDetails', fieldErrors, { minChars: 3 })]),
      localityDetails: new FormControl(this.autofill.location()?.area ?? '', [createTextFieldValidator('localityDetails', fieldErrors, { minChars: 5 })]),
      landmark: new FormControl('', [createTextFieldValidator('landmark', fieldErrors)]),
    });
    let confirmResult = firstConfirmResult;
    while (confirmResult) {
      const isEditRequest = confirmResult === LocationSheetResult.Edit;
      this.logAutofillEvent(isEditRequest ? 'location_edit_clicked' : 'location_confirmed');
      const focus = isEditRequest || !this.autofill.location()?.area ? AutofillFocus.Area : AutofillFocus.Building;
      const remainingResult = await this.openSheet(RemainingDetailsSheetComponent, { ...AUTOFILL_SHEET_CONFIG, data: { form, fieldErrors, focus } });
      if (remainingResult === RemainingDetailsSheetResult.Save) {
        this.logAutofillEvent('remaining_details_saved');
        this.saveAutofilledAddress(form);
        return;
      }
      this.logAutofillEvent('remaining_details_dismissed');
      confirmResult = await this.openSheet(AddressLocationSheetComponent, { ...AUTOFILL_SHEET_CONFIG, viewContainerRef: this.viewContainerRef });
    }
    this.logAutofillEvent('location_declined');
  }

  private async openSheet<T>(component: ComponentType<T>, config: MatBottomSheetConfig): Promise<unknown> {
    const sheetRef = this.bottomSheet.open(component, config);
    const sheet = sheetRef.instance;
    this.currentComponent.set(sheet);
    const result = await firstValueFrom(sheetRef.afterDismissed());
    this.currentComponent.remove(sheet);
    return result;
  }

  private saveAutofilledAddress(form: FormGroup): void {
    const location = this.autofill.location();
    const contactName = this.user()?.get('PatientName') ?? '';
    const mobileNumber = String(this.user()?.get('MobileNumber') ?? '');
    const isNameValid = !createNameValidator(createAddressFieldErrors())(new FormControl(contactName));
    const isPhoneValid = /^\d{10}$/.test(mobileNumber);
    if (!isNameValid || !isPhoneValid || !location?.pincode || !location.city || !location.state) {
      this.fallBackToAddressPage(form);
      return;
    }
    const localityDetails: string = form.controls.localityDetails.value;
    const address: AddressBookI = {
      contactName,
      mobileNumber,
      alternateNumber: undefined,
      buildingDetails: form.controls.buildingDetails.value,
      localityDetails,
      landmark: form.controls.landmark.value,
      zipCode: location.pincode,
      city: location.city,
      state: location.state,
      default: true,
      latLong: this.autofill.coordinates,
      reverseGeocodeAPIResponse: this.autofill.reverseGeocodeResponse,
      labels: [localityDetails === location.area ? 'autofill_location' : 'autofill_location_edited'],
    };
    this.autofillSaveInProgress.set(true);
    this.store.dispatch(fromActions.AddressBookSaveBegin({ addressId: undefined, address, skipNavigationBack: true, setAsDeliveryAddress: true }));
    this.store.select(fromSelectors.selectAddressSaveInProcess).pipe(
      filter((inProcess: boolean): boolean => !inProcess),
      take(1),
      switchMap((): Observable<AddressBookI> => this.store.select(fromSelectors.selectDefaultAddress).pipe(take(1))),
    ).subscribe((savedAddress: AddressBookI): void => {
      this.autofillSaveInProgress.set(false);
      if (!savedAddress?.objectId) return;
      this.connectionService.navigateToURL(this.autofill.confirmAddressUrl(this.route.snapshot.queryParams));
    });
  }

  private fallBackToAddressPage(form: FormGroup): void {
    const location = this.autofill.location();
    if (!location) return;
    this.handoff.set({
      location,
      coordinates: this.autofill.coordinates,
      reverseGeocodeResponse: this.autofill.reverseGeocodeResponse,
      buildingDetails: form.controls.buildingDetails.value,
      landmark: form.controls.landmark.value,
      localityDetails: form.controls.localityDetails.value,
    });
    this.navigateToAddressEdit();
  }

  private notifyDetectFailure(reason: DetectFailure): void {
    this.eventLoggerService.cleverTapEvent('location_detect_failed', JSON.stringify({ ...this.autofillEventContext(), reason }));
    const message = reason === DetectFailure.PermissionDenied ? LOCATION_PERMISSION_DENIED : NOT_ABLE_TO_DETECT_LOCATION;
    this.broadcast.broadcast('NOTIFY', { message });
  }

  private logAutofillEvent(name: string): void {
    this.eventLoggerService.cleverTapEvent(name, JSON.stringify(this.autofillEventContext()));
  }

  private autofillEventContext(): Record<string, unknown> {
    return {
      addressPageLocationFlow: this.experimentService.getAddressPageLocationFlow(),
      reviewOrderCta: true,
      entry: REVIEW_ORDER_ENTRY,
    };
  }
}
